<template>
  <transition
    appear
    name="modal"
  >
    <section
      ref="modal"

      :class="classes"

      role="dialog"
      aria-labelledby="cordel-modal-base-title-text"
      tabindex="-1"

      @click.self="close"
      @keydown.esc.exact="close"
    >
      <div class="cordel-modal-base-box">
        <typography-title
          class="cordel-modal-base-title"
          hierarchy="h1"
        >
          <span
            id="cordel-modal-base-title-text"
            class="cordel-modal-base-title-text"
          >
            <span
              v-if="prefix"
              class="cordel-modal-base-title-prefix"
            >
              {{ prefix }}
            </span>

            {{ title }}
          </span>

          <button
            v-if="closable"
            ref="close"
            class="cordel-modal-base-close"

            aria-label="Fechar"

            @click="close"
            @keydown.shift.tab.exact.prevent="loop"
          >
            <font-awesome-icon icon="times" />
          </button>
        </typography-title>

        <div
          v-if="$slots.subheader"
          class="cordel-modal-base-subheader"
        >
          <slot name="subheader" />
        </div>

        <div
          ref="content"
          class="cordel-modal-base-content"
          @scroll="scroll"
        >
          <slot />
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
  import TypographyTitle from '@cordel/components/Typography/Title.vue';

  export default {
    name: 'CordelModalBase',
    components: {
      TypographyTitle,
    },
    props: {
      title: {
        type: String,
        default: '',
      },
      prefix: {
        type: String,
        default: '',
      },
      marginless: {
        type: Boolean,
        default: false,
      },
      paddingless: {
        type: Boolean,
        default: false,
      },
      closable: {
        type: Boolean,
        default: true,
      },
    },
    computed: {
      classes() {
        const classes = ['cordel-modal-base'];

        if (this.marginless) {
          classes.push('cordel-modal-base-marginless');
        }

        if (this.paddingless) {
          classes.push('cordel-modal-base-paddingless');
        }

        return classes;
      },
    },
    mounted() {
      const {
        modal,
      } = this.$refs;

      if (modal) {
        modal.focus();
      }
    },
    methods: {
      close() {
        if (this.closable) {
          this.$emit('close');
        }
      },
      scroll(e) {
        this.$emit('scroll', e);
      },
      loop() {
        this.$emit('loop');
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-modal-base {
    --color: var(--cordel-legacy-color-constant-dark);

    background-color: var(--cordel-legacy-color-neutral-backdrop);
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: var(--cordel-legacy-z-index-max);
  }

  .cordel-modal-base-box {
    align-items: stretch;
    background-color: var(--cordel-legacy-color-constant-light);
    border-radius: var(--cordel-legacy-border-radius-small);
    box-shadow: var(--cordel-legacy-box-shadow-around-2);
    color: var(--cordel-legacy-color-constant-dark);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    left: 50%;
    max-height: calc(100% - (2 * var(--cordel-legacy-spacing-xl)));
    max-width: var(--cordel-legacy-spacing-popup);
    position: fixed;
    top: var(--cordel-legacy-spacing-xl);
    transform: translateX(-50%);
    width: calc(100% - (2 * var(--cordel-legacy-spacing-xl)));

    @media all and (max-width: $cordel-breakpoint-mobile) {
      max-height: calc(
        100%
        - env(safe-area-inset-top, var(--cordel-legacy-spacing-xl))
        - env(safe-area-inset-bottom, var(--cordel-legacy-spacing-xl))
      );
      top: env(safe-area-inset-top, var(--cordel-legacy-spacing-xl));
      width: calc(100% - (2 * var(--cordel-legacy-spacing-s)));
    }
  }

  .cordel-modal-base-close {
    @include cordel-input-reset;

    --compensation: calc(var(--cordel-legacy-spacing-giga) / 3);

    box-shadow: 0 var(--cordel-legacy-border-width-thin) transparent;
    cursor: pointer;
    font-size: var(--cordel-legacy-font-size-xl);
    margin: calc(-1 * var(--compensation));
    margin-left: calc(var(--cordel-legacy-spacing-giga) - var(--compensation));
    padding: var(--compensation);
    transition: color .1s,
                box-shadow .1s;

    &:focus {
      box-shadow: 0 var(--cordel-legacy-border-width-thin) var(--cordel-legacy-color-highlight-primary);
      color: var(--cordel-legacy-color-highlight-primary);
    }
  }

  .cordel-modal-base-title {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin: var(--cordel-legacy-spacing-giga) var(--cordel-legacy-spacing-giga) var(--cordel-legacy-spacing-ultra);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: var(--cordel-legacy-spacing-xl) var(--cordel-legacy-spacing-s) var(--cordel-legacy-spacing-giga);
    }
  }

  .cordel-modal-base-title-text {
    flex: 1;
  }

  .cordel-modal-base-title-prefix {
    position: absolute;
    left: -9999px;
  }

  .cordel-modal-base-subheader {
    --font-weight: var(--cordel-legacy-font-weight-regular);

    margin: 0 var(--cordel-legacy-spacing-giga);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: 0 var(--cordel-legacy-spacing-s);
    }
  }

  .cordel-modal-base-content {
    --font-weight: var(--cordel-legacy-font-weight-regular);

    flex: 1;
    overflow-y: auto;

    padding: 0 var(--cordel-legacy-spacing-giga) var(--cordel-legacy-spacing-giga);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: 0 var(--cordel-legacy-spacing-s) var(--cordel-legacy-spacing-xl);
    }

    .cordel-modal-base-marginless & {
      padding-left: 0;
      padding-right: 0;
    }

    .cordel-modal-base-paddingless & {
      padding-bottom: 0;
    }
  }
</style>
